import React, { useState, useEffect, useCallback, useRef } from "react";
import { useApi } from "../../../api/useApi";
import Spinner from "../../../components/Loaders/Spinner";
import { standardsListForBulk } from "../../../api/ruleQueries";
import { setRuleStandardEnabledFlag } from "../../../api/ruleMutations";
import ErrorMessages from "../../../components/Notifications/ErrorMessages";
import Button from "../../../components/Button";
import { filterDisabled } from "../../../common/helpers/util";
import Modal from "../../../components/Modal";
import DataTable from "../../../components/Table/DataTable";
import { ruleStandardById } from "../../../common/paths";
import SearchPolicies from "../../../components/Widgets/DataSourceWidgets/SeachPolicies";
import FilterByTags from "../../../components/Tags/FilterByTags";
import columnSort from "../../../components/Table/helpers/columnSort";
import StyledLink from "../../../components/StyledLink";
import { v1 as uuid } from "uuid";
import {
  BusinessRuleSourceValues,
  BusinessRuleOperations,
  BusinessRuleTargetValues,
  BusinessRuleVariables,
  BusinessRuleVariableOperations,
  ColumnModifiers,
} from "../../../components/Forms/Rules/buildingBlocks";

const DataSourceRulesListWidget = ({ setCurrentRules, setShowBulkImport }) => {
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [rules, setRules] = useState([]); // Initialize `rules` first
  const [showConfirm, setShowConfirm] = useState(false);
  const [ruleToDelete, setruleToDelete] = useState(null);

  //Filter Tags
  const [tagFilter, setTagFilter] = useState([]);
  const [exactMatchTagFilter, setExactMatchTagFilter] = useState(false);
  const [manuallySelected, setManuallySelected] = useState(null);

  const ref = useRef(null);

  const importRules = (policies) => {
    const updatedPolicies = policies.map((p) => {
      let newCards = [];

      const isDuplicateColumnRule = p.node.id === -2;
      const isDuplicateRecordRule = p.node.id === -3;

      const sorted = p.node.latestVersion.fragments.sort(function (a, b) {
        return a.executionOrder - b.executionOrder;
      });

      sorted.forEach((frag, i) => {
        const fragType = frag.typeInformation[0].typeValue;
        if (fragType === 1) {
          // Condition
          const conditionNames = ["If", "Else If", "Else", "End If", "Then"];
          const fragment = {
            typeInformation: frag.typeInformation,
            id: uuid(),
            name: conditionNames[frag.typeInformation[1].typeValue - 1],
            def: "conditionOperators",
          };

          newCards.push(fragment);
        } else if (fragType === 2) {
          // Condition Operator

          const conditionOperatorNames = ["And", "Or", "(", ")"];
          const fragment = {
            typeInformation: frag.typeInformation,
            id: uuid(),
            name: conditionOperatorNames[frag.typeInformation[1].typeValue - 1],
            def: "conditionOperators",
          };
          newCards.push(fragment);
        } else if (fragType === 4) {
          // Source

          //Map Source Dropdown to value for react-select
          const source = { typeInformation: frag.typeInformation };

          source.typeInformation[1].typeValue = BusinessRuleSourceValues.find(
            (sv) => sv.value === source.typeInformation[1].typeValue
          );

          if (source.typeInformation[2]) {
            source.typeInformation[2].typeValue = ColumnModifiers.find(
              (sv) => sv.value === source.typeInformation[2].typeValue
            );
          }

          const operation = { typeInformation: sorted[i + 1].typeInformation };
          operation.typeInformation[1].typeValue = BusinessRuleOperations.find(
            (sv) => sv.value === operation.typeInformation[1].typeValue
          );

          //Handle the case in which operation is set to IsEmpty, No Target should be sent or loaded
          let target;
          if (!isDuplicateColumnRule && !isDuplicateRecordRule) {
            const businessRule = operation.typeInformation[1].typeValue.value;

            const isByAmountOrisByPercentage =
              operation.typeInformation[2]?.typeValue === 4 ||
              operation.typeInformation[2]?.typeValue === 5 ||
              operation.typeInformation[2]?.typeValue === 7 ||
              operation.typeInformation[2]?.typeValue === 8;

            if (
              (businessRule === 6 && !isByAmountOrisByPercentage) ||
              (businessRule === 14 && !isByAmountOrisByPercentage) ||
              (businessRule === 15 && !isByAmountOrisByPercentage)
            ) {
              target = null;
            } else {
              target = { typeInformation: sorted[i + 2].typeInformation };
              target.typeInformation[1].typeValue =
                BusinessRuleTargetValues.find(
                  (sv) => sv.value === target.typeInformation[1].typeValue
                );

              if (
                target.typeInformation[1] &&
                target.typeInformation[1].typeValue &&
                target.typeInformation[1].typeValue.value &&
                target.typeInformation[1].typeValue.value === 3
              ) {
                target.typeInformation[1].fragmentValue =
                  BusinessRuleVariables.find(
                    (sv) =>
                      sv.value ===
                      Number(target.typeInformation[1].fragmentValue)
                  );
              }

              if (target.typeInformation[2]) {
                target.typeInformation[2].typeValue =
                  BusinessRuleVariableOperations.find(
                    (sv) => sv.value === target.typeInformation[2].typeValue
                  );
              }

              if (target.typeInformation[3]) {
                target.typeInformation[3].typeValue = ColumnModifiers.find(
                  (sv) => sv.value === target.typeInformation[3].typeValue
                );
              }
            }
          }

          const fragment = {
            id: uuid(),
            name: "Fragment",
            def: "fragment",
            source: source,
            operation: operation,
            target: target,
          };
          newCards.push(fragment);
        }
      });

      const policyObj = {
        standard: newCards,
        name: p.node.name,
        description: p.node.description,
      };

      return policyObj;
    });

    setCurrentRules(updatedPolicies);
    setShowBulkImport(false);
  };

  const updateLocally = useCallback(
    (node) => {
      if (!node?.id) {
        console.warn("Node object must have a valid id.");
        return;
      }

      const updatedRules = rules.map((rule) => {
        if (rule?.node?.id === node.id) {
          return {
            ...rule,
            node: {
              ...rule.node,
              selected: !selectedPolicies.find((sp) => sp.node.id === node.id), // Toggle selection
            },
          };
        }
        return rule;
      });

      setRules(updatedRules); // Update rules to reflect local changes

      // Update selectedPolicies state
      setSelectedPolicies((prevSelected) =>
        prevSelected.some((sp) => sp.node.id === node.id)
          ? prevSelected.filter((sp) => sp.node.id !== node.id)
          : [...prevSelected, { node }]
      );
    },
    [rules, selectedPolicies]
  );

  const updateAllLocally = useCallback(
    (rules) => {
      const localRules = [...rules];
      const updatedRules = localRules.map((rule) => {
        return {
          ...rule,
          node: {
            ...rule.node,
            selected: true, // Toggle selection
          },
        };
      });

      setRules(updatedRules); // Update rules to reflect local changes

      // Update selectedPolicies state
      setSelectedPolicies(updatedRules);
    },
    [rules, selectedPolicies]
  );

  //Set Function to enable child component to fetch parent reference for width
  const getTableWidth = () => {
    return ref?.current?.offsetWidth ?? 0;
  };

  //Init Data Fetch
  const [
    {
      loading: availableStandardsLoading,
      errors: availableStandardsErrors,
      data: standards,
    },
    doFetch,
  ] = useApi();

  const totalCount = standards?.availableBusinessRuleStandards?.totalCount;
  const pageInfo = standards?.availableBusinessRuleStandards?.pageInfo;

  //Fetch for Table Paged
  const fetchData = React.useCallback(
    ({ pageSize, cursor, sortBy }) => {
      const sortedObject = columnSort(sortBy);
      doFetch({
        query: standardsListForBulk,
        variables: {
          first: pageSize,
          tagFilter: {
            tagIds: [...tagFilter?.map((tf) => tf.tagId)],
            exactMatch: exactMatchTagFilter,
          },
          after: cursor,
          standardType: "RULE",
          where: {
            enabled: { eq: true },
          },
          ...(sortedObject
            ? {
                order: {
                  ...sortedObject,
                },
              }
            : {
                order: {
                  name: "ASC",
                },
              }),
        },
      });
    },
    [doFetch, tagFilter, exactMatchTagFilter]
  );

  useEffect(() => {
    if (ruleToDelete) {
      setShowConfirm(true);
    } else {
      setShowConfirm(false);
    }
  }, [ruleToDelete]);

  //remove rules state

  const [
    { loading: removeRuleDataLoading, data: removeRuleData },
    removeRuleApi,
  ] = useApi();

  //handle remove rule update
  useEffect(() => {
    if (removeRuleData) {
      //set filtered rule
      setRules((prevRules) =>
        prevRules.filter(
          (rule) =>
            rule?.node?.id !== removeRuleData?.setRuleStandardEnabledFlag?.id
        )
      );
      setruleToDelete(null);
    }
  }, [removeRuleData]);

  //callback for removing a row
  const removeRuleStandard = useCallback(
    (templateId) => {
      const variables = { id: templateId, enabled: false };
      removeRuleApi({ query: setRuleStandardEnabledFlag, variables });
    },
    [removeRuleApi]
  );

  //set rules into local state so we can mutate on remove
  useEffect(() => {
    if (standards) {
      const availableStandards =
        standards?.availableBusinessRuleStandards?.edges ?? [];

      // Merge fetched data with current local selections
      const mergedRules = filterDisabled(availableStandards).map((rule) => {
        const isSelected = selectedPolicies.some(
          (sp) => sp.node?.id === rule?.node?.id
        );

        return {
          ...rule,
          node: {
            ...rule.node,
            selected: isSelected, // Sync with local state
          },
        };
      });

      setRules(mergedRules);
    }
  }, [standards, selectedPolicies]);

  if (availableStandardsErrors)
    return <ErrorMessages errors={availableStandardsErrors} />;

  const columnsData = [
    {
      Header: "Name",
      id: "name",
      accessor: (d) => d?.node?.name,
      Cell: ({ row: { original } }) => {
        const isSelected = original?.node?.id
          ? selectedPolicies.some((sp) => sp.node?.id === original.node.id)
          : false;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "1rem" }}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => updateLocally(original?.node)}
              />
            </div>

            <StyledLink
              to={ruleStandardById(original?.node?.id)}
              data-testid="policyLink"
            >
              {original?.node?.name}
            </StyledLink>
          </div>
        );
      },
    },
    {
      Header: "Tags",
      id: "tags",
      Cell: ({ row: { original } }) => {
        return (
          <div>
            {original?.node?.tagInstances?.map((ti, i) => {
              const isLast = original?.node?.tagInstances?.length - 1 <= i;
              return (
                <div
                  onClick={() => setManuallySelected([ti])}
                  style={{
                    display: "inline-block",
                    background: "#e6e6e6",
                    padding: "0.2rem",
                    paddingLeft: ".4rem",
                    paddingRight: ".4rem",
                    fontSize: ".8rem",
                    marginRight: isLast ? "" : ".5rem",
                    marginBottom: isLast ? "" : ".5rem",
                    cursor: "pointer",
                  }}
                >
                  {ti?.tag?.name}
                </div>
              );
            })}
          </div>
        );
      },
    },
  ];

  return (
    <>
      {showConfirm ? (
        <Modal
          title={`Confirm Rule Removal`}
          hide={() => setruleToDelete(null)}
        >
          <p>Are you sure you wish to remove this Rule?</p>
          <div>
            <Button
              type="button"
              list="true"
              disabled={removeRuleDataLoading}
              danger
              onClick={() => removeRuleStandard(ruleToDelete)}
            >
              {removeRuleDataLoading ? <Spinner /> : "Yes"}
            </Button>
            <Button
              type="button"
              disabled={removeRuleDataLoading}
              onClick={() => {
                setruleToDelete(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      ) : null}

      <div>Selected {selectedPolicies?.length} policies</div>

      <Button
        type="button"
        onClick={() => importRules(selectedPolicies)}
        disabled={!selectedPolicies?.length}
      >
        Finalize Selection
      </Button>

      <div style={{ display: "flex" }}>
        <div style={{ flex: 1, marginRight: "1rem" }}>
          <SearchPolicies ruleType={1} />
        </div>
        <div style={{ flex: 1, marginTop: "1rem", marginBottom: "1rem" }}>
          <FilterByTags
            currentTags={manuallySelected}
            updateTags={setTagFilter}
            tagType="DATA_SOURCE"
            exactMatch={setExactMatchTagFilter}
          />
        </div>
      </div>

      <Button type="button" onClick={() => updateAllLocally(rules)}>
        Select All in View
      </Button>

      <div ref={ref}>
        <DataTable
          defaultPageSize={10}
          fetchData={fetchData}
          loading={availableStandardsLoading}
          pageInfo={pageInfo}
          totalCount={totalCount}
          data={rules}
          columns={columnsData}
        />
      </div>
    </>
  );
};

export default DataSourceRulesListWidget;
